import { useState, useEffect, useCallback } from 'react';
import { X, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import type { ContentItem, ContentType, ContentChannel, ContentStatus } from '../../types/content';
import { AGENTS, DEPARTMENTS } from '../../utils/agents';
import { useContentStore } from '../../stores/contentStore';

const CONTENT_TYPES: ContentType[] = ['social', 'email', 'blog', 'ad', 'video'];
const CHANNELS: ContentChannel[] = ['instagram', 'tiktok', 'pinterest', 'email', 'blog', 'facebook', 'etsy'];

const STATUS_OPTIONS: Array<{ key: ContentStatus; label: string }> = [
  { key: 'idea', label: 'Idea' },
  { key: 'draft', label: 'Draft' },
  { key: 'review', label: 'Review' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'published', label: 'Published' },
];

interface ContentDetailModalProps {
  item: ContentItem;
  onClose: () => void;
}

export default function ContentDetailModal({ item, onClose }: ContentDetailModalProps) {
  const updateContent = useContentStore((s) => s.updateContent);
  const deleteContent = useContentStore((s) => s.deleteContent);

  const [title, setTitle] = useState(item.title);
  const [contentType, setContentType] = useState<ContentType>(item.content_type);
  const [channel, setChannel] = useState(item.channel || '');
  const [status, setStatus] = useState<ContentStatus>(item.status);
  const [assignedAgent, setAssignedAgent] = useState(item.assigned_agent || '');
  const [department, setDepartment] = useState(item.department || '');
  const [dueDate, setDueDate] = useState(item.due_date || '');
  const [publishDate, setPublishDate] = useState(item.publish_date || '');
  const [notes, setNotes] = useState(item.notes || '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') handleClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  function handleAgentChange(slug: string) {
    setAssignedAgent(slug);
    if (slug) {
      const agent = AGENTS.find((a) => a.slug === slug);
      if (agent) {
        setDepartment(agent.department);
      }
    }
  }

  async function handleSave() {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await updateContent(item.id, {
        title: title.trim(),
        content_type: contentType,
        channel: channel || null,
        status,
        assigned_agent: assignedAgent || null,
        department: department || null,
        due_date: dueDate || null,
        publish_date: publishDate || null,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (err) {
      console.error('Failed to update content:', err);
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await deleteContent(item.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete content:', err);
    }
  }

  function formatTimestamp(ts: string | null): string {
    if (!ts) return '-';
    return format(new Date(ts), 'MMM d, yyyy h:mm a');
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative bg-bg-secondary border border-border-subtle rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-text-primary">Content Details</h2>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-bg-tertiary text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-2">Status</label>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setStatus(opt.key)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    status === opt.key
                      ? 'bg-accent-purple text-white'
                      : 'bg-bg-tertiary text-text-muted hover:text-text-secondary'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Content Type</label>
              <select
                value={contentType}
                onChange={(e) => setContentType(e.target.value as ContentType)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                {CONTENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Channel</label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                <option value="">None</option>
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Department</label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                <option value="">None</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Assigned Agent</label>
              <select
                value={assignedAgent}
                onChange={(e) => handleAgentChange(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                <option value="">Unassigned</option>
                {AGENTS.map((a) => (
                  <option key={a.slug} value={a.slug}>
                    {a.name} -- {a.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
              />
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Publish Date</label>
              <input
                type="date"
                value={publishDate}
                onChange={(e) => setPublishDate(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple resize-none"
              rows={4}
            />
          </div>

          <div className="border-t border-border-subtle pt-3 mt-1">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-text-muted block">Created</span>
                <span className="text-text-secondary">{formatTimestamp(item.created_at)}</span>
              </div>
              <div>
                <span className="text-text-muted block">Updated</span>
                <span className="text-text-secondary">{formatTimestamp(item.updated_at)}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center mt-2">
            <button
              onClick={handleDelete}
              className={`flex items-center gap-1.5 text-sm transition-colors px-3 py-2 rounded-md ${
                confirmDelete
                  ? 'bg-error/15 text-error'
                  : 'text-text-muted hover:text-error'
              }`}
            >
              <Trash2 className="h-4 w-4" />
              {confirmDelete ? 'Confirm Delete' : 'Delete'}
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="text-text-muted hover:text-text-secondary text-sm transition-colors px-3 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !title.trim()}
                className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
